import React, { useEffect, useState } from 'react';
import { Input, Button, CheckboxTable, Select, PaperClip } from 'components';
import { RootState } from 'reducers';
import { RouteComponentProps } from 'react-router-dom';
import { Globals, Constants } from 'utils';
import moment from 'moment';
import { SignatureService, RevisionService, AvailabilityService } from 'services';
import { connect } from 'react-redux';
import { UploadIcon, CloseIcon, AddIcon, UserIcon } from 'assets/icons';
import { useApacuana } from 'contexts/ApacuanaContext';
import { PDFDocument } from 'pdf-lib';

interface Signer {
  id: number;
  public_id: string;
  full_name: string;
  photo?: string;
}

interface Props extends RootState {
  onClose?: any;
  item?: any;
  setLoading?: any;
  addSigner?: any;
  getDocs?: any;
  signDocument?: any;
  isExistCertificate?: any;
  apacuana?: any;
}

class ModalCreate extends React.Component<Props> {
  state = {
    form: {
      area_id: '',
      person_type_id: '',
      file: '',
    },
    areas: [],
    person_types: [],
    isSigned: false,
    certificatePassword: '',
    file: null,
    signer_code: '',
    signers: [] as Signer[],
    searching_signer: false,
  };

  componentDidMount() {
    // this.load();
  }

  // Deshabilitado: el nuevo flujo de invitar firmantes (LT-305) ya no pide
  // área/tipo de persona en este modal. Se deja comentado (no se borra) por
  // si se retoma el flujo del "Frente A".
  // load = async () => {
  //   const res: any = await AvailabilityService.getAreas({
  //     service_id: Constants.SERVICES.SIGN,
  //     lawyer_type: 'both',
  //     user_id: this.props.user?.id,
  //   });
  //   this.setState({
  //     areas: res.areas || [],
  //   });
  // };

  setFile(e: any) {
    // console.log(e);
    this.setState({
      file: e,
    });
  }

  base64ToFile(base64Data: string, filename: string, mimeType: string): File {
    // 1. Eliminamos el prefijo (ej: "data:image/png;base64,") si existe
    const base64Content = base64Data.includes(',') ? base64Data.split(',')[1] : base64Data;

    // 2. Decodificamos la cadena limpia
    const byteCharacters = atob(base64Content);

    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], { type: mimeType });

    return new File([blob], filename, { type: mimeType });
  }

  change = (e: any, callback = () => {}) => {
    this.setState(
      {
        form: {
          ...this.state.form,
          [e.target.name]: e.target.value,
        },
      },
      callback,
    );
  };

  changeSignerCode = (e: any) => {
    this.setState({ signer_code: e.target.value });
  };

  // Busca el usuario por su código público (U-123 / A-45) y, si el backend lo
  // valida como elegible, lo agrega a la lista de firmantes a invitar.
  addSignerByCode = async () => {
    const code = this.state.signer_code.trim();
    if (!code) return;

    if (this.state.signers.some((s) => s.public_id?.toLowerCase() === code.toLowerCase())) {
      Globals.showError('Este firmante ya fue agregado.');
      return;
    }

    this.setState({ searching_signer: true });
    try {
      const candidate: any = await SignatureService.searchUser({
        code,
        user_id: this.props.user?.id,
      });

      if (this.state.signers.some((s) => s.id === candidate.id)) {
        Globals.showError('Este firmante ya fue agregado.');
        return;
      }

      this.setState({
        signers: [...this.state.signers, candidate],
        signer_code: '',
      });
    } catch (error) {
      // El mensaje de error del backend ya se muestra vía el interceptor de Api.
    } finally {
      this.setState({ searching_signer: false });
    }
  };

  removeSigner = (id: number) => {
    this.setState({
      signers: this.state.signers.filter((s) => s.id !== id),
    });
  };

  // Riesgo aceptado (documentado en el plan): si falla esta llamada tras haber
  // guardado el documento, la solicitud queda creada sin firmantes invitados.
  // No se revierte el `save()` — el usuario deberá reintentar desde el listado.
  inviteSigners = async (signature_id: any, signers: Signer[]) => {
    if (!signature_id || signers.length === 0) return;

    await SignatureService.inviteSigners({
      signature_id,
      user_ids: signers.map((s) => s.id),
      invited_by: this.props.user?.id,
    });
  };

  submit = async (e: any) => {
    this.props.setLoading(true);
    e.preventDefault();

    const { isSigned, certificatePassword, signers } = this.state;
    const fileToProcess: any = this.state.form.file ? (this.state.form.file as any).value : null; // Archivo original del state

    if (!fileToProcess) {
      Globals.showError('Por favor, suba el documento para continuar');
      this.props.setLoading(false);
      return;
    }

    if (signers.length === 0) {
      Globals.showError('Debe agregar al menos un firmante para continuar');
      this.props.setLoading(false);
      return;
    }

    if (isSigned && certificatePassword.length < 8) {
      Globals.showError('Debe ingresar su contraseña de certificado electrónico.');
      this.props.setLoading(false);
      return;
    }

    try {
      if (isSigned) {
        // --- PROCESAMIENTO DEL PDF PARA AÑADIR HOJA ---
        const existingPdfBytes = await fileToProcess.arrayBuffer();
        const pdfDoc = await PDFDocument.load(existingPdfBytes);

        // Añadir una página en blanco al final
        pdfDoc.addPage();
        const pageCount = pdfDoc.getPageCount();
        const modifiedPdfBytes: any = await pdfDoc.save();

        // Crear un nuevo objeto File con la hoja extra
        const modifiedFile = new File([new Blob([modifiedPdfBytes])], fileToProcess.name, {
          type: 'application/pdf',
        });

        // --- CONFIGURACIÓN DE FIRMA EN LA NUEVA ÚLTIMA HOJA ---
        const signerData = {
          name: modifiedFile.name || '',
          reference: '',
          typedoc: this.props.apacuana.user?.full_document.split('-')[0],
          doc: this.props.apacuana.user?.full_document.split('-')[1],
          signature: [
            {
              page: pageCount, // Usamos la página recién creada
              x: 0.2, // 20% horizontal
              y: 0.7, // 70% vertical (ajusta según prefieras)
            },
          ],
          file: modifiedFile,
        };

        // Registro del firmante
        await this.props.addSigner(signerData);

        // Obtención de documentos preparados
        const docToSignRes = await this.props.getDocs();
        const doc = docToSignRes.data.records[0];

        const signatureObject = {
          pin: certificatePassword,
          signature: {
            id: doc.signaturedata.id,
            positions: doc.signaturedata.positions,
          },
          document: modifiedFile,
        };

        // Ejecución de la firma electrónica
        const signDoc = await this.props.signDocument(signatureObject);

        if (!signDoc || !signDoc.data?.result) {
          throw new Error('Credenciales incorrectas o error en el servidor de firma.');
        }

        // Conversión de Base64 (resultado) a File
        const pdfFileFinal = this.base64ToFile(
          signDoc.data.result,
          modifiedFile.name,
          'application/pdf',
        );

        // Guardar en base de datos
        const savedSignature: any = await SignatureService.save({
          hasFile: true,
          ...this.state.form,
          user_id: this.props.user?.id,
          // @ts-ignore
          file: pdfFileFinal,
        });

        await this.inviteSigners(savedSignature?.id, signers);

        Globals.showSuccess('Se ha enviado su documento firmado');
        this.props.onClose();
      } else {
        // Flujo sin firma (Normal)
        const savedSignature: any = await SignatureService.save({
          hasFile: true,
          ...this.state.form,
          user_id: this.props.user?.id,
          // @ts-ignore
          file: fileToProcess,
        });

        await this.inviteSigners(savedSignature?.id, signers);

        Globals.showSuccess('Se ha enviado su documento');
        this.props.onClose();
      }
    } catch (error: any) {
      console.error('Error en submit:', error);
      Globals.showError(error.message || 'Error al enviar la solicitud.');
    } finally {
      this.props.setLoading(false);
    }
  };
  render() {
    const { user: apacuanaUser } = this.props.apacuana || {};
    const { signer_code, signers, searching_signer } = this.state;
    return (
      <div id='modal-create-signature'>
        <h3>Firma Electrónica</h3>
        {/* Selección de área/tipo de persona deshabilitada (LT-305): el nuevo
            flujo de invitar firmantes ya no la requiere. Se deja comentada,
            no se borra, por si se retoma el "Frente A". */}
        {/* <p>Seleccione el área del documento</p>
        <div className='row row-persons'>
          {this.state.person_types.map((item: any) => {
            return (
              <div className='col-md-6'>
                <div
                  className={`item-person ${
                    this.state.form.person_type_id == item.id ? 'active' : ''
                  }`}
                  onClick={() =>
                    this.setState({
                      form: {
                        ...this.state.form,
                        person_type_id: item.id,
                      },
                    })
                  }
                >
                  <img src={Constants.BASE_PUBLIC + item.icon} />
                  <p>{item.name}</p>
                </div>
              </div>
            );
          })}
        </div> */}
        <form className='white-container' onSubmit={this.submit}>
          {/* <Select
            color='white'
            placeholder='Seleccionar'
            label='Seleccione tipo de derecho'
            name='area_id'
            onChange={this.change}
            value={this.state.form.area_id}
            options={this.state.areas.map((item: any) => {
              return {
                value: item.id,
                label: item.name,
              };
            })}
          /> */}
          {this.state.form.file == '' && (
            <div className='file-container'>
              <div className='container-icon'>
                <img src={UploadIcon} />
                <p>Subir el documento que desea agregar</p>
              </div>
              <PaperClip
                setFile={(e: any) => this.setFile(e)}
                formats={['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx']}
                successText='Subir documento'
                pendingText='Subir documento'
                value={this.state.form.file}
                onChange={(e: any) => {
                  this.setState({
                    form: {
                      ...this.state.form,
                      file: e.target,
                    },
                  });
                }}
              />
            </div>
          )}
          {this.state.form.file != '' && (
            <div className='file-uploaded'>
              <img src={UploadIcon} />
              <p>Documento</p>
              <button
                className='btn'
                onClick={() => {
                  this.setState({
                    form: {
                      ...this.state.form,
                      file: '',
                    },
                    file: null,
                  });
                }}
              >
                <img src={CloseIcon} />
              </button>
            </div>
          )}

          <div className='mt-3 container-signers'>
            <label className='form-label'>Invitar firmantes</label>
            <p className='text-secondary small' style={{ textAlign: 'left' }}>
              Ingrese el código público del cliente o abogado que también debe firmar este
              documento.
            </p>
            <div className='row' style={{ alignItems: 'flex-end' }}>
              <div className='col-9'>
                <Input
                  color='white'
                  name='signer_code'
                  label='Código de usuario'
                  placeholder='U-123'
                  value={signer_code}
                  onChange={this.changeSignerCode}
                />
              </div>
              <div className='col-3'>
                <button
                  type='button'
                  className='btn btn-report active'
                  style={{ width: '100%', marginBottom: 18, padding: 5 }}
                  disabled={!signer_code.trim() || searching_signer}
                  onClick={this.addSignerByCode}
                >
                  <img src={AddIcon} style={{ maxWidth: 16, marginRight: 6, color: '#ffffff' }} />
                  Agregar
                </button>
              </div>
            </div>

            {signers.length > 0 && (
              <div className='signers-list' style={{ marginTop: 12 }}>
                {signers.map((signer) => (
                  <div
                    key={signer.id}
                    className='signer-item'
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '8px 12px',
                      borderRadius: 8,
                      border: '1px solid #eee',
                      marginBottom: 8,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src={signer.photo ? Constants.BASE_STORAGE + signer.photo : UserIcon}
                        style={{ width: 32, height: 32, borderRadius: '50%', marginRight: 10 }}
                      />
                      <div>
                        <p style={{ margin: 0, fontWeight: 600, color: '#222222ff' }}>
                          {signer.full_name}
                        </p>
                        <p style={{ margin: 0, fontSize: '0.8rem', color: '#888' }}>
                          {signer.public_id}
                        </p>
                      </div>
                    </div>
                    <button
                      type='button'
                      className='btn'
                      style={{ padding: 0 }}
                      onClick={() => this.removeSigner(signer.id)}
                    >
                      <img src={CloseIcon} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {this.state.form.file != '' && this.state.signers.length > 0 && (
            <div className='text-center' style={{ marginTop: 16 }}>
              <Button
                type='submit'
                disabled={this.state.isSigned && this.state.certificatePassword.length < 8}
              >
                Enviar
              </Button>
            </div>
          )}

          {this.state.form.file != '' && this.state.signers.length === 0 && (
            <p className='text-secondary small text-center' style={{ marginTop: 16 }}>
              Agregue al menos un firmante para poder enviar la solicitud.
            </p>
          )}

          {apacuanaUser?.apacuanaId && this.props.isExistCertificate && (
            <div className='mt-3'>
              <label htmlFor='signedSwitch' className='form-label'>
                ¿Desea firmar este documento?
              </label>
              <div className='form-check form-switch'>
                <input
                  className='form-check-input'
                  type='checkbox'
                  role='switch'
                  id='signedSwitch'
                  checked={this.state.isSigned} // Conectado al estado
                  onChange={(e) => this.setState({ isSigned: e.target.checked })} // Actualiza el estado
                />
                <label className='form-check-label' htmlFor='signedSwitch'>
                  Sí, deseo firmarlo yo también
                </label>
              </div>

              {this.state.isSigned && (
                <div className='mt-4 p-3 bg-light border rounded'>
                  <h5 className='mb-3'>Contraseña de Certificado</h5>
                  <p className='text-secondary small'>
                    Para incluir su firma electrónica, debe proporcionar la contraseña que usó para
                    generar su certificado.
                  </p>
                  <Input
                    type='password'
                    color='white'
                    name='certificatePassword'
                    label='Contraseña del Certificado'
                    placeholder='Ingrese su contraseña'
                    value={this.state.certificatePassword}
                    onChange={(e: any) => this.setState({ certificatePassword: e.target.value })}
                  />
                </div>
              )}
            </div>
          )}
        </form>
      </div>
    );
  }
}

const ModalCreateApacuana = (props: Props) => {
  const apacuanaContext: any = useApacuana();
  const [loading, setLoading] = useState(false);
  const [isExist, setIsExist] = useState(false);
  console.log(apacuanaContext.user);
  const init = async () => {
    setLoading(true);
    try {
      if (apacuanaContext?.isInitialized) {
        const response = await apacuanaContext?.sdk?.isCertificateInDevice();
        setIsExist(response.data.isExist);
      }
    } catch (e) {
      console.log({ e });
    } finally {
      setLoading(false);
    }
  };

  const addSigner = async (signerData: any) => {
    setLoading(true);
    try {
      const result = await apacuanaContext?.sdk?.addSigner(signerData);
      return result;
    } catch (e) {
      console.error('SDK ERROR: Fallo al ejecutar addSigner.', e);
      return null;
    }
  };

  const signDocument = async (data: any) => {
    try {
      const result = await apacuanaContext?.sdk?.signDocument(data);
      return result;
    } catch (e) {
      console.error('SDK ERROR: Fallo al ejecutar signDocument.', e);
      return null;
    }
  };

  const getDocs = async () => {
    try {
      const result = await apacuanaContext?.sdk?.getDocsByCustomer({
        page: 0,
        size: 1,
      });
      return result;
    } catch (e) {
      console.error('SDK ERROR: Fallo al ejecutar getDocs.', e);
      return null;
    }
  };

  useEffect(() => {
    if (loading) {
      Globals.setLoading();
    } else {
      Globals.quitLoading();
    }
  }, [loading]);

  useEffect(() => {
    init();
  }, []);

  return (
    <>
      <ModalCreate
        {...props}
        setLoading={setLoading}
        addSigner={addSigner}
        getDocs={getDocs}
        signDocument={signDocument}
        isExistCertificate={isExist}
        apacuana={apacuanaContext}
      />
    </>
  );
};

export default connect((state: RootState) => {
  return {
    user: state.user,
  };
})(ModalCreateApacuana);
